import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Tooltip,
    IconButton,
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MainCard from '../../../components/cards/MainCard';
import TimelineIcon from '@mui/icons-material/Timeline';
import HelpIcon from "@mui/icons-material/HelpOutline";
import DownloadIcon from '@mui/icons-material/Download';
import CheckIcon from '@mui/icons-material/Check';
import { useDynamicBreakpoints } from '../../../utils/breakpointUtils';
import { useSelector } from 'react-redux';
import Certificate from './Certificate';
import GlobalPagination from '../../../components/GlobalPagination';
import { APP_URL } from '../../../config';
import { ReactComponent as CheckCircleIcon } from '../../../assets/images/icons/tickcircle.svg';

const ActivityTimeline = ({
    fetchedUserCompletedCourses = [],
    showCourseContent,
    user,
    loading,
    tenancyBasics = null,
    tenancyData = null
}) => {
    const theme = useTheme();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { matchDownSM, matchDownMD } = useDynamicBreakpoints(drawerOpen);
    const [page, setPage] = useState(1);
    const itemsPerPage = 2;

    // Download feedback states
    const [downloadingCourses, setDownloadingCourses] = useState(new Set());
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const formatDate = (date) => {
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
        }).replace(/\//g, '.');
    };

    // Handle certificate download with feedback
    const handleDownloadCertificate = async (course) => {
        const courseId = course.id || course.universityCourseName;

        // Add course to downloading set
        setDownloadingCourses(prev => new Set([...prev, courseId]));

        // Show download commencing message
        setSnackbarMessage('Download commencing...');
        setSnackbarOpen(true);

        try {
            await Certificate.generateCertificate(
                course,
                user,
                tenancyData,
                tenancyBasics,
                theme.palette.primary.main,
                theme.palette.primary.light,
                APP_URL
            );

            // Show success message
            setSnackbarMessage('Certificate downloaded successfully!');
            setSnackbarOpen(true);

            // Remove from downloading set after showing success (similar to copy button timing)
            setTimeout(() => {
                setDownloadingCourses(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(courseId);
                    return newSet;
                });
            }, 1500);

        } catch (error) {
            console.error('Download failed:', error);
            setSnackbarMessage('Download failed. Please try again.');
            setSnackbarOpen(true);

            // Remove from downloading set on error
            setDownloadingCourses(prev => {
                const newSet = new Set(prev);
                newSet.delete(courseId);
                return newSet;
            });
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    // Only show completed courses, sorted by completion date (most recent first)
    const activities = fetchedUserCompletedCourses.map(course => ({
        ...course,
        type: 'completed',
        date: new Date(course.dateCompleted)
    })).sort((a, b) => b.date - a.date);

    // Calculate visible activities (following CourseCarousel pattern)
    const visibleActivities = (activities) => {
        const start = (page - 1) * itemsPerPage;
        return activities.slice(start, start + itemsPerPage);
    };

    const handleChangePage = (event, value) => {
        setPage(value);
    };

    const currentActivities = visibleActivities(activities);

    return (
        <>
            <MainCard
                content={false}
                sx={{
                    width: "100%",
                    bgcolor: "background.default",
                    alignSelf: 'flex-start',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 470
                }}
                border={false}
                boxShadow={true}
            >
                {/* Header Section */}
                <Box sx={{
                    p: 2  // Consistent 16px padding on all sides
                }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                            <TimelineIcon sx={{ mr: 1, fontSize: '1.5rem' }} color="primary" />
                            Activity Timeline
                        </Typography>

                        <Tooltip title="History of completed courses. Revisiting a completed course does not override initial course scores.">
                            <IconButton size="small">
                                <HelpIcon sx={{ color: "secondary.main" }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{
                        height: { xs: 'auto', sm: '325px' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2
                    }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                            Loading Activity Timeline...
                        </Typography>
                    </Box>
                ) : activities.length === 0 ? (
                    <Box sx={{
                        height: { xs: 'auto', sm: '325px' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2
                    }}>
                        <Typography variant="body2" color="textSecondary">
                            No completed courses to display
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* Content Section */}
                        <Box sx={{
                            position: 'relative',
                            width: '100%',
                            height: { xs: 'auto', sm: '325px' },
                            p: 2,
                            pt: 0,
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Timeline Content Area */}
                            <Box sx={{
                                position: 'relative'
                            }}>
                                {/* Timeline line */}
                                {currentActivities.length > 0 && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: matchDownSM ? 15 : 20,
                                            top: 0,
                                            height: currentActivities.length > 1 ? '95%' : '50%',
                                            width: 2,
                                            bgcolor: 'divider',
                                            zIndex: 0
                                        }}
                                    />
                                )}

                                {currentActivities.map((activity, index) => {
                                    const courseId = activity.id || activity.universityCourseName;
                                    const isDownloading = downloadingCourses.has(courseId);

                                    return (
                                        <Box key={`${activity.type}-${activity.id}`} sx={{
                                            position: 'relative',
                                            mb: index < currentActivities.length - 1 ? 4 : 2,
                                            pl: matchDownSM ? 5 : 6
                                        }}>
                                            {/* Timeline icons */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: matchDownSM ? 5 : 8,
                                                    top: 20, // Adjusted to align perfectly with "Course completed" text
                                                    width: matchDownSM ? 20 : 25,
                                                    height: matchDownSM ? 20 : 25,
                                                    borderRadius: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    zIndex: 1,
                                                    bgcolor: 'white'
                                                }}
                                            >
                                                <CheckCircleIcon sx={{
                                                    fontSize: 35,
                                                    color: '#33BD95'
                                                }} />
                                            </Box>

                                            {/* Activity content */}
                                            <Box>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    mb: 0.5, // Reduced margin to sit directly above "Course completed"
                                                    minHeight: 20 // Reduced height
                                                }}>
                                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                                        {formatDate(activity.date)}
                                                    </Typography>
                                                </Box>

                                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                    Course completed
                                                </Typography>

                                                <Typography variant="body2" sx={{ mb: 2 }}>
                                                    {activity.universityCourseName}
                                                </Typography>

                                                <Box sx={{
                                                    display: 'flex',
                                                    gap: 1
                                                }}>
                                                    <Button
                                                        variant="contained"
                                                        sx={{
                                                            bgcolor: '#4CAF50',
                                                            color: 'white',
                                                            fontWeight: 600,
                                                            fontSize: '0.875rem',
                                                            py: 1,
                                                            width: '75%'
                                                        }}
                                                        onClick={() => showCourseContent(activity)}
                                                    >
                                                        REVISIT
                                                    </Button>

                                                    {/* Download Certificate Button with Loading State */}

                                                    {activity.correctAnswers >= 80 && (
                                                        <Tooltip title={isDownloading ? "Downloading..." : "Download Certificate"}>
                                                            <IconButton
                                                                onClick={() => handleDownloadCertificate(activity)}
                                                                disabled={isDownloading}
                                                                sx={{
                                                                    bgcolor: 'primary.main',
                                                                    color: 'white',
                                                                    width: '15%',
                                                                    height: 40,
                                                                    '&:hover': {
                                                                        bgcolor: 'primary.main',
                                                                        transform: isDownloading ? 'none' : 'scale(1.05)'
                                                                    },
                                                                    '&:disabled': {
                                                                        bgcolor: 'primary.main',
                                                                        color: 'white'
                                                                    },
                                                                    transition: 'all 0.2s ease-in-out'
                                                                }}
                                                            >
                                                                {isDownloading ? (
                                                                    <CheckIcon />
                                                                ) : (
                                                                    <DownloadIcon />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>)}
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* Bottom Pagination Section */}
                        <Box sx={{
                            p: 2,
                            pt: 0
                        }}>
                            <GlobalPagination
                                count={Math.ceil(activities.length / itemsPerPage)}
                                page={page}
                                onChange={handleChangePage}
                                showFirstButton={false}
                                showLastButton={false}
                            />
                        </Box>
                    </>
                )}
            </MainCard>

            {/* Download Feedback Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarMessage.includes('failed') ? 'error' :
                        snackbarMessage.includes('successfully') ? 'success' : 'info'}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>


        </>
    );
};

export default ActivityTimeline;